// src/pages7-recipie-ai/Profile.js
import React, { useEffect, useState, useCallback } from "react";
import Support from "./Contact";
import config from "../config.json";
import { useNavigate } from "react-router-dom";
import { fetchUsers, deleteUser, sendEmail, UpdateData, maskEmail } from "../service/APIService";
import { Modal } from "react-bootstrap";
import secureLocalStorage from "react-secure-storage";
import {
  FaUserShield,
  FaTimes,
  FaSignOutAlt,
  FaTrash,
  FaSignInAlt,
  FaCamera,
  FaGooglePlay,
  FaApple,
  FaShareAlt,
  FaStar,
  FaShieldAlt,
  FaQuestionCircle,
  FaSun,
  FaMoon,
  FaAdjust,
} from "react-icons/fa";
import { forceLogoutAndPurge, getStoredProfilePic, saveStoredProfilePic } from "../utils/sessionUtils";
import { getStoredTheme, setThemePreference } from "../utils/themeUtils";

const SENDER_EMAIL = "Recipe AI <support@theuniquecreations.com>";

const Profile = () => {
  const navigate = useNavigate();

  const [loggedInUser, setLoggedInUser] = useState("");
  const [loggedInUserName, setLoggedInUserName] = useState("");
  const [role, setRole] = useState("");
  const [showRateModal, setShowRateModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(5);
  const [toastMessage, setToastMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [enteredEmail, setEnteredEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [serverOtp, setServerOtp] = useState("");
  const [finalConfirm, setFinalConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => getStoredTheme());

  const storedEmail =
    secureLocalStorage.getItem("loggedInUserEmail") ||
    (typeof localStorage !== "undefined" && localStorage.getItem("loggedInUserEmail")) ||
    "";
  const rawUser = secureLocalStorage.getItem("loggedInUser");
  const parsedUser = rawUser ? (typeof rawUser === "string" ? JSON.parse(rawUser) : rawUser) : null;
  const isGuest = !storedEmail || storedEmail === "guest" || parsedUser?.email === "guest";

  const [profilePic, setProfilePic] = useState(() => getStoredProfilePic(storedEmail) || parsedUser?.profilePic || "");

  useEffect(() => {
    const isBio = secureLocalStorage.getItem("biometricEnabled") === "true";
    setBiometricEnabled(isBio);

    if (parsedUser && parsedUser.email !== "guest") {
      setLoggedInUser(parsedUser.email || "");
      setLoggedInUserName(parsedUser.name || "");
      setRole(parsedUser.role || "");
    } else if (isGuest) {
      const rawGuest = secureLocalStorage.getItem("guestUser");
      const guest = rawGuest ? (typeof rawGuest === "string" ? JSON.parse(rawGuest) : rawGuest) : null;
      setLoggedInUser(guest?.email || "guest");
      setLoggedInUserName(guest?.name || "Guest User");
      setRole("");
    }
  }, [isGuest]);

  useEffect(() => {
    const handleProfileUpdate = (e) => {
      if (e?.detail?.profilePic !== undefined) {
        setProfilePic(e.detail.profilePic);
      }
    };
    window.addEventListener("user-profile-updated", handleProfileUpdate);
    return () => window.removeEventListener("user-profile-updated", handleProfileUpdate);
  }, []);

  const handleProfilePicUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file (PNG, JPG, or WEBP).");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const size = 180;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");

        let sx = 0,
          sy = 0,
          sWidth = img.width,
          sHeight = img.height;
        if (img.width > img.height) {
          sx = (img.width - img.height) / 2;
          sWidth = img.height;
        } else if (img.height > img.width) {
          sy = (img.height - img.width) / 2;
          sHeight = img.width;
        }

        ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, size, size);
        const compressedDataUrl = canvas.toDataURL("image/jpeg", 0.85);

        saveStoredProfilePic(compressedDataUrl, loggedInUser);
        setProfilePic(compressedDataUrl);

        if (!isGuest && loggedInUser && loggedInUser !== "guest") {
          try {
            const dbUser = await fetchUsers(loggedInUser);
            if (dbUser && dbUser.id) {
              await UpdateData({ ...dbUser, profilePic: compressedDataUrl });
            }
          } catch (err) {
            console.warn("Could not save profile pic to DB:", err);
          }
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveProfilePic = async () => {
    saveStoredProfilePic("", loggedInUser);
    setProfilePic("");
    if (!isGuest && loggedInUser && loggedInUser !== "guest") {
      try {
        const dbUser = await fetchUsers(loggedInUser);
        if (dbUser && dbUser.id) {
          await UpdateData({ ...dbUser, profilePic: "" });
        }
      } catch (err) {
        console.warn("Could not remove profile pic from DB:", err);
      }
    }
  };

  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");
  const androidPackage = "com.recipeaiapp";
  const androidStoreUrl = config.androidurl || `https://play.google.com/store/apps/details?id=${androidPackage}`;
  const iosStoreUrl = config.iosurl || "https://apps.apple.com/us/app/recipe-ai-cooking-assistant/id6741011254";
  const storeUrl = isAndroid ? androidStoreUrl : iosStoreUrl;
  const storeReviewUrl = isAndroid
    ? `market://details?id=${androidPackage}`
    : storeUrl.includes("?")
    ? `${storeUrl}&action=write-review`
    : `${storeUrl}?action=write-review`;

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => {
          setToastMessage("Link copied to clipboard for sharing!");
          setTimeout(() => setToastMessage(""), 2500);
        },
        () => {
          setToastMessage("Link copied to clipboard!");
          setTimeout(() => setToastMessage(""), 2500);
        }
      );
    } else {
      setToastMessage("Link copied to clipboard!");
      setTimeout(() => setToastMessage(""), 2500);
    }
  };

  const handleShareApp = async () => {
    const shareData = {
      title: config.apptitle || "Recipe AI",
      text: "Generate delicious recipes from ingredients you have in your fridge with AI!",
      url: storeUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err.name !== "AbortError") {
          console.log("Error sharing:", err);
        }
      }
    }
    copyToClipboard(storeUrl);
  };

  const handleOpenExternalReview = (targetUrl) => {
    const target = targetUrl || storeReviewUrl;
    setShowRateModal(false);

    if (window.ReactNativeWebView) {
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: "OPEN_EXTERNAL", url: target }));
      } catch (err) {
        console.log("ReactNativeWebView postMessage error:", err);
      }
      setTimeout(() => {
        if (document.visibilityState === "visible") {
          window.location.href = target;
        }
      }, 1500);
      return;
    }

    try {
      const win = window.open(target, "_blank", "noopener,noreferrer");
      if (!win) {
        window.location.href = target;
      }
    } catch (e) {
      window.location.href = target;
    }
  };

  const handleGuestExitToLogin = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    forceLogoutAndPurge();
    window.location.replace("/");
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    forceLogoutAndPurge();
    window.location.replace("/");
  };

  // Delete profile flow
  const handleSendDeleteOtp = async () => {
    const cleanEntered = (enteredEmail || "").trim().toLowerCase();
    const cleanLoggedIn = (loggedInUser || "").trim().toLowerCase();

    if (cleanEntered !== cleanLoggedIn) {
      alert("Entered email does not match logged-in email.");
      return;
    }

    const isDemoAppleUser = cleanEntered === "demo@apple.com";
    if (isDemoAppleUser) {
      setServerOtp("123456");
      setOtpSent(true);
      alert("Demo OTP is 123456");
      return;
    }

    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    const requestDateTime = new Date().toLocaleString();
    const htmlContent = `
      <div style="font-family: sans-serif; max-width: 440px; margin: 0 auto; padding: 20px; border-radius: 12px; background: #0f1f14; color: #fff;">
        <h2 style="color: #ef4444; margin-top: 0;">Recipe AI Profile Deletion</h2>
        <p>Your OTP code to permanently delete your profile is:</p>
        <div style="background: #182e1f; padding: 16px; border-radius: 8px; text-align: center; margin: 20px 0;">
          <span style="font-size: 32px; font-weight: bold; letter-spacing: 6px; color: #ef4444;">${otp}</span>
        </div>
        <p style="color: #94a3b8; font-size: 12px;">Requested on ${requestDateTime}. This action is irreversible.</p>
      </div>
    `;

    const emailData = {
      sender: SENDER_EMAIL,
      recipient: cleanLoggedIn,
      subject: "Recipe AI - Profile Deletion OTP",
      cc: cleanLoggedIn,
      body: htmlContent,
    };

    try {
      setLoading(true);
      await sendEmail(emailData);
      setServerOtp(otp);
      setOtpSent(true);
      alert("OTP sent to your email address: " + cleanLoggedIn);
    } catch (err) {
      alert("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    const isConfirmed = window.confirm("Are you sure you want to permanently delete your profile? ❌");
    if (!isConfirmed) return;

    setLoading(true);
    try {
      const isDemo = (loggedInUser || "").trim().toLowerCase() === "demo@apple.com";
      const isOtpValid = otpInput.trim() === serverOtp.trim() || (isDemo && otpInput.trim() === "123456");

      if (enteredEmail.trim().toLowerCase() === loggedInUser.trim().toLowerCase() && isOtpValid) {
        const users = await fetchUsers(loggedInUser);
        if (users && users.id) {
          await deleteUser(users.id);
        }
        forceLogoutAndPurge();
        alert("Profile deleted successfully.");
        window.location.replace("/");
      } else {
        alert("Email or OTP did not match. Please verify your details.");
      }
    } catch (error) {
      alert("Failed to delete user profile.");
    } finally {
      setLoading(false);
    }
  };

  const isAdminUser = loggedInUser === "vbalakumar.cse@gmail.com" || role === "admin";

  return (
    <div className="container py-3 mb-5 pb-5">
      {/* Toast Alert */}
      {toastMessage && (
        <div
          className="position-fixed top-0 start-50 translate-middle-x mt-3 p-3 bg-dark text-white rounded-pill shadow"
          style={{ zIndex: 1100, fontSize: "13px" }}
        >
          {toastMessage}
        </div>
      )}

      {/* User Card */}
      <div className="app-card p-3 mb-3 border">
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative">
            {profilePic ? (
              <img
                src={profilePic}
                alt="Profile"
                className="rounded-circle shadow-sm"
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  border: "2px solid #22c55e",
                }}
              />
            ) : (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                style={{
                  width: "60px",
                  height: "60px",
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  fontSize: "22px",
                }}
              >
                {(loggedInUserName || (isGuest ? "G" : "U")).charAt(0).toUpperCase()}
              </div>
            )}
            {!isGuest && (
              <label
                htmlFor="profile-pic-input"
                className="position-absolute bottom-0 end-0 rounded-circle d-flex align-items-center justify-content-center bg-success text-white p-0 shadow"
                style={{
                  width: "24px",
                  height: "24px",
                  cursor: "pointer",
                  border: "2px solid #ffffff",
                }}
                title="Update Profile Picture"
              >
                <FaCamera size={11} />
                <input
                  id="profile-pic-input"
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleProfilePicUpload}
                />
              </label>
            )}
          </div>

          <div className="flex-grow-1 min-w-0">
            <h5 className="mb-0 fw-bold text-dark text-truncate">
              {loggedInUserName || (isGuest ? "Guest User" : "User")}
            </h5>
            <small className="text-secondary text-truncate d-block">
              {isGuest ? "Guest Mode" : maskEmail(loggedInUser)}
            </small>
            {!isGuest && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <label
                  htmlFor="profile-pic-input"
                  className="btn btn-sm btn-light border py-0 px-2 text-success fw-semibold"
                  style={{ fontSize: "11px", cursor: "pointer" }}
                >
                  <FaCamera size={10} className="me-1" /> {profilePic ? "Change Photo" : "Upload Photo"}
                </label>
                {profilePic && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0 small text-decoration-none"
                    style={{ fontSize: "11px" }}
                    onClick={handleRemoveProfilePic}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account Info Details */}
      <div className="app-card p-3 mb-3 border">
        <div className="py-2 border-bottom">
          <span className="text-secondary small d-block">Name</span>
          <span className="fw-semibold text-dark">{loggedInUserName || (isGuest ? "Guest User" : "")}</span>
        </div>
        <div className="py-2 border-bottom">
          <span className="text-secondary small d-block">Email</span>
          <span className="fw-semibold text-dark">
            {isGuest ? "Guest Account (Not Signed In)" : maskEmail(loggedInUser)}
          </span>
        </div>
        <div className="py-2 border-bottom">
          <span className="text-secondary small d-block">Biometrics / Face ID</span>
          <span className="text-success small fw-semibold">
            {isGuest ? "Sign in to enable" : biometricEnabled ? "Enabled" : "Available in Mobile App"}
          </span>
        </div>
        {/* Appearance / Theme Toggle Segment */}
        <div className="pt-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <span className="fw-semibold text-dark small d-block">Appearance</span>
              <span className="text-secondary" style={{ fontSize: "12px" }}>
                Light, Dark, or System Auto
              </span>
            </div>
            <span className="badge rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: "10px", textTransform: "capitalize" }}>
              {currentTheme}
            </span>
          </div>

          <div
            className="theme-toggle-capsule d-flex p-1 rounded-pill"
            style={{
              background: "rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(0, 0, 0, 0.06)",
              gap: "4px",
            }}
          >
            {[
              { id: "light", label: "Light", icon: <FaSun size={12} className="me-1.5" /> },
              { id: "dark", label: "Dark", icon: <FaMoon size={12} className="me-1.5" /> },
              { id: "system", label: "System", icon: <FaAdjust size={12} className="me-1.5" /> },
            ].map((t) => {
              const isActive = currentTheme === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setCurrentTheme(t.id);
                    setThemePreference(t.id);
                  }}
                  className={`btn btn-sm flex-fill rounded-pill d-flex align-items-center justify-content-center fw-semibold ${
                    isActive ? "shadow-sm" : ""
                  }`}
                  style={{
                    fontSize: "12px",
                    padding: "6px 12px",
                    border: "none",
                    background: isActive ? "#ffffff" : "transparent",
                    color: isActive ? "#0f172a" : "#64748b",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {t.icon}
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sharing & Rating Rows */}
      <div className="app-card p-0 mb-3 border overflow-hidden">
        <div
          className="d-flex align-items-center justify-content-between p-3 border-bottom"
          role="button"
          tabIndex={0}
          onClick={handleShareApp}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-success"
              style={{ width: "38px", height: "38px", background: "rgba(34, 197, 94, 0.12)" }}
            >
              <FaShareAlt size={16} />
            </div>
            <div>
              <div className="fw-semibold text-dark small">Share Recipe AI</div>
              <div className="text-secondary" style={{ fontSize: "12px" }}>
                Invite friends to cook with whatever is in their fridge
              </div>
            </div>
          </div>
          <span className="text-secondary">&rarr;</span>
        </div>

        <div
          className="d-flex align-items-center justify-content-between p-3"
          role="button"
          tabIndex={0}
          onClick={() => setShowRateModal(true)}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-warning"
              style={{ width: "38px", height: "38px", background: "rgba(234, 179, 8, 0.12)" }}
            >
              {isAndroid ? <FaGooglePlay size={16} /> : <FaApple size={18} />}
            </div>
            <div>
              <div className="fw-semibold text-dark small">Rate and Review</div>
              <div className="text-secondary" style={{ fontSize: "12px" }}>
                {isAndroid ? "Share your feedback on Google Play" : "Share your feedback on the App Store"}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <span className="text-warning me-2 fw-bold">★★★★★</span>
            <span className="text-secondary">&rarr;</span>
          </div>
        </div>
      </div>

      {/* Guest Mode Banner */}
      {isGuest && (
        <div
          className="p-3 my-3 rounded-3 text-center border"
          style={{
            background: "rgba(34, 197, 94, 0.08)",
            borderColor: "rgba(34, 197, 94, 0.25)",
          }}
        >
          <p className="small text-dark mb-2 fw-medium">You are using Recipe AI in Guest mode.</p>
          <button
            type="button"
            className="btn btn-success btn-sm px-4 py-2 rounded-pill fw-bold shadow-sm"
            onClick={handleGuestExitToLogin}
          >
            Log in to sync & save your recipes
          </button>
        </div>
      )}

      {/* Admin Quick Action */}
      {isAdminUser && (
        <div className="app-card p-3 mb-3 border text-center">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <FaUserShield className="text-primary" size={18} />
              <span className="fw-bold text-dark">Admin Console</span>
            </div>
            <a href="/admin/dashboard" className="btn btn-sm btn-primary rounded-pill px-3">
              Go to Dashboard
            </a>
          </div>
        </div>
      )}

      {/* Actions & Settings Group */}
      <div className="app-card p-0 mb-3 border overflow-hidden">
        {/* Support & Feedback Action Row */}
        <div
          className="d-flex align-items-center justify-content-between p-3 border-bottom"
          role="button"
          tabIndex={0}
          onClick={() => setShowModal(true)}
          style={{ cursor: "pointer", transition: "background 0.2s ease" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center text-success"
              style={{ width: "38px", height: "38px", background: "rgba(34, 197, 94, 0.12)" }}
            >
              <FaQuestionCircle size={16} />
            </div>
            <div>
              <div className="fw-semibold text-dark small">Support & Feedback</div>
              <div className="text-secondary" style={{ fontSize: "12px" }}>
                Need help or have suggestions? Reach out to our team
              </div>
            </div>
          </div>
          <span className="text-secondary">&rarr;</span>
        </div>

        {/* Log In / Log Out Action Row */}
        <div
          className="d-flex align-items-center justify-content-between p-3 border-bottom"
          role="button"
          tabIndex={0}
          onClick={() => (isGuest ? handleGuestExitToLogin() : setShowLogoutModal(true))}
          style={{ cursor: "pointer", transition: "background 0.2s ease" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className={`rounded-circle d-flex align-items-center justify-content-center ${isGuest ? "text-success" : "text-danger"}`}
              style={{
                width: "38px",
                height: "38px",
                background: isGuest ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)",
              }}
            >
              <FaSignOutAlt size={16} />
            </div>
            <div>
              <div className={`fw-semibold small ${isGuest ? "text-success" : "text-danger"}`}>
                {isGuest ? "Log In" : "Log Out"}
              </div>
              <div className="text-secondary" style={{ fontSize: "12px" }}>
                {isGuest ? "Sign in to save your recipes and preferences" : "Sign out of your account on this device"}
              </div>
            </div>
          </div>
          <span className="text-secondary">&rarr;</span>
        </div>

        {/* Delete Profile Action Row (Non-guest only) */}
        {!isGuest && (
          <div>
            <div
              className="d-flex align-items-center justify-content-between p-3"
              role="button"
              tabIndex={0}
              onClick={() => {
                setShowDeleteSection(!showDeleteSection);
                setFinalConfirm(false);
              }}
              style={{ cursor: "pointer", transition: "background 0.2s ease" }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-danger"
                  style={{ width: "38px", height: "38px", background: "rgba(239, 68, 68, 0.12)" }}
                >
                  <FaTrash size={14} />
                </div>
                <div>
                  <div className="fw-semibold text-danger small">Delete Profile</div>
                  <div className="text-secondary" style={{ fontSize: "12px" }}>
                    Permanently delete account and all data
                  </div>
                </div>
              </div>
              <span className={`text-secondary transition-transform ${showDeleteSection ? "rotate-90" : ""}`}>&rarr;</span>
            </div>

            {showDeleteSection && (
              <div className="p-3 border-top bg-white bg-opacity-75">
                <h6 className="text-danger fw-bold mb-1">Delete Profile</h6>
                <p className="text-secondary small mb-3">
                  This action is permanent and will remove all saved recipe and calorie history.
                </p>
                <div className="mb-3">
                  <label className="small text-secondary mb-1 fw-semibold">Enter your email to confirm:</label>
                  <input
                    type="email"
                    value={enteredEmail}
                    onChange={(e) => setEnteredEmail(e.target.value)}
                    className="form-control rounded-pill px-3"
                    placeholder="your.email@example.com"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm rounded-pill px-3 mt-2 fw-semibold"
                    onClick={handleSendDeleteOtp}
                    disabled={loading || !enteredEmail.trim()}
                  >
                    {loading ? "Please wait..." : otpSent ? "Resend OTP" : "Send OTP"}
                  </button>
                </div>

                {otpSent && (
                  <div className="mt-3 pt-3 border-top">
                    <label className="small text-secondary mb-1 fw-semibold">Enter 6-digit OTP:</label>
                    <input
                      type="number"
                      value={otpInput}
                      onChange={(e) => setOtpInput(e.target.value)}
                      className="form-control rounded-pill px-3 my-2"
                      placeholder="Enter OTP"
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm px-4 rounded-pill fw-semibold"
                      onClick={() => {
                        setFinalConfirm(true);
                        setShowDeleteSection(false);
                      }}
                      disabled={!otpInput.trim()}
                    >
                      Confirm Deletion
                    </button>
                  </div>
                )}
              </div>
            )}

            {finalConfirm && (
              <div className="p-4 border-top border-danger text-center bg-danger bg-opacity-10">
                <p className="text-danger fw-bold mb-2">Are you absolutely sure you want to delete your profile?</p>
                <button
                  type="button"
                  className="btn btn-danger btn-sm px-4 rounded-pill fw-bold"
                  onClick={handleDeleteProfile}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Yes, permanently delete my profile"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Support Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
        <div className="modal-header border-bottom">
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "1.2rem" }}>🎧</span>
            <h5 className="modal-title text-dark fw-bold mb-0">Help & Support</h5>
          </div>
          <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
        </div>
        <Modal.Body className="p-3">
          <Support isModal={true} />
        </Modal.Body>
      </Modal>

      {/* Rating Modal */}
      <Modal show={showRateModal} onHide={() => setShowRateModal(false)} centered>
        <div className="modal-header border-bottom">
          <h5 className="modal-title text-dark fw-bold">Enjoying Recipe AI?</h5>
          <button type="button" className="btn-close" onClick={() => setShowRateModal(false)} />
        </div>
        <Modal.Body className="text-center py-4">
          <div className="d-flex justify-content-center gap-2 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar
                key={star}
                size={30}
                style={{ cursor: "pointer" }}
                className={star <= selectedRating ? "text-warning" : "text-muted"}
                onClick={() => setSelectedRating(star)}
              />
            ))}
          </div>
          <p className="text-secondary small mb-4">
            Tap a star to rate. Your 5-star review helps us keep adding fresh recipe ideas!
          </p>
          <button
            type="button"
            className="btn btn-success w-100 py-2 rounded-pill fw-bold shadow-sm"
            onClick={() => handleOpenExternalReview()}
          >
            Leave a Review on {isAndroid ? "Google Play" : "App Store"}
          </button>
        </Modal.Body>
      </Modal>

      {/* Logout Modal */}
      <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered>
        <div className="modal-header border-bottom">
          <h5 className="modal-title text-dark fw-bold">Log Out</h5>
          <button type="button" className="btn-close" onClick={() => setShowLogoutModal(false)} />
        </div>
        <Modal.Body className="py-3">
          <p className="mb-0 text-secondary">
            Are you sure you want to log out of Recipe AI? Your remembered email will remain saved for your next login.
          </p>
        </Modal.Body>
        <div className="modal-footer border-top">
          <button type="button" className="btn btn-light rounded-pill px-3" onClick={() => setShowLogoutModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger rounded-pill px-3" onClick={confirmLogout}>
            Log Out
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
